import { ClientService } from '../../../shared/api/ClientService';
import type {
  ActionReason,
  CommandStatus,
  EventStatus,
  ICommandFilters,
  ICommandSort,
  IEventFilters,
  IEventSort,
  IReplayItemPresentation,
  IStatusPresentation,
  ITextDrafts,
  ReplayItemStatus,
  SortColumnKey,
  EventSortColumnKey,
  SortDirection,
} from './types';

const Status = ClientService.InsolvCommandStatus;
const InboxStatus = ClientService.InsolvInboxEventStatus;

export const COMMANDS_TAB_KEY = 'commands';
export const EVENTS_TAB_KEY = 'events';

export const SUMMARY_QUERY_KEY = 'summary';
export const COMMANDS_QUERY_KEY = 'commands';
export const COMMAND_QUERY_KEY = 'command';
export const EVENTS_QUERY_KEY = 'events';

export const INTEGRATION_DEFAULT_PAGE_SIZE = 20;
// The API refuses a page size above 100 (validation error).
export const INTEGRATION_MAX_PAGE_SIZE = 100;
export const INTEGRATION_PAGE_SIZE_OPTIONS = ['10', '20', '50', String(INTEGRATION_MAX_PAGE_SIZE)];
export const COMMAND_MAX_ACTION_IDS = 100;

export const COMMAND_OLDEST_DUE_WARNING_SECONDS = 900;
// Twice the default interval of the reconciler (15 minutes): later than that, it is running late.
export const WATERMARK_AGE_WARNING_SECONDS = 1800;
export const SECONDS_PER_MINUTE = 60;
export const MINUTES_PER_HOUR = 60;
export const SECONDS_PER_HOUR = SECONDS_PER_MINUTE * MINUTES_PER_HOUR;

export const DISPLAY_DATE_FORMAT = 'YYYY-MM-DD HH:mm:ss';
export const EMPTY_VALUE = '-';
export const LABEL_SEPARATOR = ' · ';

export const DEFAULT_COMMAND_FILTERS: ICommandFilters = {
  commandType: '',
  fileName: '',
  createdRange: null,
  heldOnly: false,
};
export const DEFAULT_EVENT_FILTERS: IEventFilters = { eventType: '', occurredRange: null };
export const COMMAND_EMPTY_TEXT_DRAFTS: ITextDrafts = { commandType: '', fileName: '' };
export const COMMAND_FILTER_IDS = {
  status: 'integration-monitoring-status',
  commandType: 'integration-monitoring-command-type',
  fileName: 'integration-monitoring-file-name',
  created: 'integration-monitoring-created',
} as const;
export const EVENT_FILTER_IDS = {
  status: 'integration-monitoring-event-status',
  eventType: 'integration-monitoring-event-type',
  occurred: 'integration-monitoring-occurred',
} as const;

export const COMMAND_SORT_KEYS: Record<SortColumnKey, string> = {
  creationTime: 'CreationTime',
  status: 'Status',
  commandType: 'CommandType',
  attempts: 'Attempts',
  nextAttemptTime: 'NextAttemptTime',
};
export const EVENT_SORT_KEYS: Record<EventSortColumnKey, string> = {
  occurredTime: 'OccurredTime',
  // The API sorts the Received column by the inbox CreationTime; ReceivedTime is not in its whitelist.
  receivedTime: 'CreationTime',
  eventType: 'EventType',
  status: 'Status',
};
export const SORT_DIRECTIONS: Record<SortDirection, string> = { ascend: 'asc', descend: 'desc' };
export const DEFAULT_COMMAND_SORT: ICommandSort = { columnKey: 'creationTime', order: 'descend' };
export const DEFAULT_EVENT_SORT: IEventSort = { columnKey: 'occurredTime', order: 'descend' };

export const DAY_UNIT = 'day';

// A new status is a compile error here until it is given a colour and its texts.
export const COMMAND_STATUS_PRESENTATION: Record<CommandStatus, IStatusPresentation> = {
  [Status.Pending]: {
    color: 'blue',
    labelKey: 'INTEGRATION_STATUS_PENDING',
    meaningKey: 'INTEGRATION_MEANING_PENDING',
  },
  [Status.Retrying]: {
    color: 'orange',
    labelKey: 'INTEGRATION_STATUS_RETRYING',
    meaningKey: 'INTEGRATION_MEANING_RETRYING',
  },
  [Status.NeedsAttention]: {
    color: 'volcano',
    labelKey: 'INTEGRATION_STATUS_NEEDS_ATTENTION',
    meaningKey: 'INTEGRATION_MEANING_NEEDS_ATTENTION',
  },
  [Status.DeadLettered]: {
    color: 'red',
    labelKey: 'INTEGRATION_STATUS_DEAD_LETTERED',
    meaningKey: 'INTEGRATION_MEANING_DEAD_LETTERED',
  },
  [Status.Succeeded]: {
    color: 'green',
    labelKey: 'INTEGRATION_STATUS_SUCCEEDED',
    meaningKey: 'INTEGRATION_MEANING_SUCCEEDED',
  },
  [Status.Duplicate]: {
    color: 'cyan',
    labelKey: 'INTEGRATION_STATUS_DUPLICATE',
    meaningKey: 'INTEGRATION_MEANING_DUPLICATE',
  },
  [Status.Cancelled]: {
    color: 'default',
    labelKey: 'INTEGRATION_STATUS_CANCELLED',
    meaningKey: 'INTEGRATION_MEANING_CANCELLED',
  },
};

export const COMMAND_STATUS_FILTER_ORDER: readonly CommandStatus[] = [
  Status.Pending,
  Status.Retrying,
  Status.NeedsAttention,
  Status.DeadLettered,
  Status.Succeeded,
  Status.Duplicate,
  Status.Cancelled,
];

export const COMMAND_RETRYABLE_STATUSES: readonly CommandStatus[] = [
  Status.Retrying,
  Status.NeedsAttention,
  Status.DeadLettered,
];
export const COMMAND_CANCELLABLE_STATUSES: readonly CommandStatus[] = [
  Status.Pending,
  Status.Retrying,
  Status.NeedsAttention,
  Status.DeadLettered,
];

export const COMMAND_ACTION_REASON_TEXT_KEYS: Record<ActionReason, string> = {
  NotFound: 'INTEGRATION_REASON_NOT_FOUND',
  InvalidState: 'INTEGRATION_REASON_INVALID_STATE',
  Conflict: 'INTEGRATION_REASON_CONFLICT',
  Failed: 'INTEGRATION_REASON_FAILED',
};

// A new inbox status is a compile error here until it is given a colour and its texts.
export const EVENT_STATUS_PRESENTATION: Record<EventStatus, IStatusPresentation> = {
  [InboxStatus.Processed]: {
    color: 'green',
    labelKey: 'INTEGRATION_EVENT_STATUS_PROCESSED',
    meaningKey: 'INTEGRATION_EVENT_MEANING_PROCESSED',
  },
  [InboxStatus.IgnoredFileNotInDebtHub]: {
    color: 'default',
    labelKey: 'INTEGRATION_EVENT_STATUS_IGNORED_FILE',
    meaningKey: 'INTEGRATION_EVENT_MEANING_IGNORED_FILE',
  },
  [InboxStatus.IgnoredNotFoundInInsolv]: {
    color: 'orange',
    labelKey: 'INTEGRATION_EVENT_STATUS_IGNORED_NOT_FOUND',
    meaningKey: 'INTEGRATION_EVENT_MEANING_IGNORED_NOT_FOUND',
  },
  [InboxStatus.IgnoredRecordNotInDebtHub]: {
    color: 'orange',
    labelKey: 'INTEGRATION_EVENT_STATUS_IGNORED_RECORD',
    meaningKey: 'INTEGRATION_EVENT_MEANING_IGNORED_RECORD',
  },
};

export const EVENT_STATUS_FILTER_ORDER: readonly EventStatus[] = [
  InboxStatus.Processed,
  InboxStatus.IgnoredFileNotInDebtHub,
  InboxStatus.IgnoredNotFoundInInsolv,
  InboxStatus.IgnoredRecordNotInDebtHub,
];

// The status of a replayed event is a free text from Insolv: only these three are known.
export const REPLAY_ITEM_PRESENTATION: Record<ReplayItemStatus, IReplayItemPresentation> = {
  Accepted: {
    color: 'green',
    labelKey: 'INTEGRATION_REPLAY_ACCEPTED',
    hintKey: 'INTEGRATION_REPLAY_ACCEPTED_HINT',
  },
  NotFound: {
    color: 'orange',
    labelKey: 'INTEGRATION_REPLAY_NOT_FOUND',
    hintKey: 'INTEGRATION_REPLAY_NOT_FOUND_HINT',
  },
  Expired: {
    color: 'red',
    labelKey: 'INTEGRATION_REPLAY_EXPIRED',
    hintKey: 'INTEGRATION_REPLAY_EXPIRED_HINT',
  },
};

export const MAX_REPLAY_IDS = 200;
export const MAX_INVALID_TOKENS_SHOWN = 5;
export const MAX_TOKEN_LENGTH_SHOWN = 40;
export const TOKEN_ELLIPSIS = '…';
export const REPLAY_TEXT_ROWS = 8;
export const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// It looks like an id but the server refuses it.
export const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';
export const ID_SEPARATORS = /[\s,;]+/;
// Ids pasted from logs often come in braces, brackets or quotes.
export const ID_WRAPPERS = /^[{}[\]()"']+|[{}[\]()"']+$/g;

export const FLAG_ON_COLOR = 'green';
export const FLAG_OFF_COLOR = 'default';
export const COMMAND_HOLDING_TAG_COLOR = 'red';
export const CATCHING_UP_TAG_COLOR = 'processing';
export const REPLAYABLE_ICON_COLOR = '#52c41a';
export const DANGER_VALUE_COLOR = '#cf1322';
export const WARNING_VALUE_COLOR = '#d46b08';

export const COMMAND_COLUMN_WIDTH = {
  selection: 48,
  fileName: 180,
  commandType: 200,
  status: 260,
  attempts: 100,
  created: 170,
  nextAttempt: 170,
  http: 80,
  lastFailure: 260,
  actions: 90,
} as const;
export const COMMAND_TABLE_SCROLL_X = Object.values(COMMAND_COLUMN_WIDTH).reduce((total, width) => total + width, 0);

export const EVENT_COLUMN_WIDTH = {
  selection: 48,
  eventId: 260,
  eventType: 220,
  occurred: 170,
  received: 170,
  status: 300,
  fileName: 180,
  canReapply: 130,
  actions: 90,
} as const;
export const EVENT_TABLE_SCROLL_X = Object.values(EVENT_COLUMN_WIDTH).reduce((total, width) => total + width, 0);

export const DETAIL_MODAL_WIDTH = 720;
export const DESCRIPTION_LABEL_WIDTH = 190;
export const RESULT_MODAL_WIDTH = 720;
export const REPLAY_MODAL_WIDTH = 880;
